// The confirmation in front of the bulk Delete on the file lists (Smart Upload
// and each data profile). Requesting a deletion is irreversible — the table's
// queue removes every row the file added and its records — so the exact files are
// named before anything is written. Deletions are whole files only (Spec 5): there
// is no unit to select. Both lists share the same eligibility rules, so the target
// collection lives here too.
import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
  CircularProgress,
} from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { tableScope } from '../../../utils/blobPaths';
import { fileDeleteBlock } from '../../../utils/fileDeletion';

const MAX_LISTED = 40;

// A file may be deleted unless it is still cleaning, its check is running or
// taking answers, or a deletion of it is already under way (utils/fileDeletion).
// Only the check's batch document says that it is taking answers, so the batch
// of every selected file still in a check is read here, as the file card follows
// it (hooks/useFileBatch). A selected file that may not be deleted is counted so
// the dialog can say it will be skipped. deleteFile judges each file again in
// its own transaction. Each target says whether its file has landed, which the
// confirmation's wording follows.
export async function collectBulkDeleteTargets({ companyId, tableId, files, selectedFileIds }) {
  const fileById = new Map(files.map((file) => [file.id, file]));
  const selectedFiles = Array.from(selectedFileIds)
    .map((id) => fileById.get(id))
    .filter((file) => file !== undefined);

  const batchIds = new Set();
  for (const file of selectedFiles) {
    if (file.batch_id != null && file.batch_status !== 'completed') batchIds.add(file.batch_id);
  }
  const batchById = new Map();
  await Promise.all(Array.from(batchIds).map(async (batchId) => {
    const batchSnapshot = await getDoc(doc(db, `${tableScope(companyId, tableId)}/batches/${batchId}`));
    batchById.set(batchId, batchSnapshot.exists() ? batchSnapshot.data() : null);
  }));

  const targets = [];
  let skipped = 0;
  for (const file of selectedFiles) {
    const batch = batchById.get(file.batch_id) ?? null;
    if (fileDeleteBlock(file, batch)) {
      skipped += 1;
    } else {
      targets.push({ id: file.id, label: file.name, landed: file.batch_status === 'completed' });
    }
  }
  return { files: targets, skipped };
}

const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

function TargetList({ items }) {
  const shown = items.slice(0, MAX_LISTED);
  return (
    <List dense disablePadding sx={{ maxHeight: 220, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
      {shown.map((item) => (
        <ListItem key={item.id} sx={{ py: 0.25 }}>
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{ variant: 'body2', noWrap: true }}
          />
        </ListItem>
      ))}
      {items.length > MAX_LISTED && (
        <ListItem sx={{ py: 0.25 }}>
          <ListItemText primary={`… and ${items.length - MAX_LISTED} more`} primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
      )}
    </List>
  );
}

function BulkDeleteConfirmDialog({ open, targets, tableName, busy = false, onCancel, onConfirm }) {
  const files = targets?.files || [];
  const skipped = targets?.skipped || 0;
  const nothingToDelete = files.length === 0;

  // The confirmation of Spec 5 section 1, as the file card words it (utils/fileDeletion),
  // with "Each file" in place of a file's name: the files are listed by name beneath it.
  // Only a landed file has rows in the table to take with it, so the middle sentences
  // are there only when a listed file has landed.
  let confirmation = 'Each file will be removed. You can always upload it again.';
  if (files.some((target) => target.landed)) {
    confirmation = `Each file will be removed, and the rows it added to ${tableName} with it. `
      + 'Answers given while it was checked stay on the other files they changed. '
      + 'You can always upload it again.';
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{nothingToDelete ? 'Nothing to delete' : `Delete ${plural(files.length, 'file')}?`}</DialogTitle>
      <DialogContent>
        {nothingToDelete ? (
          <DialogContentText>
            Every file selected is still being worked on or is already being deleted.
          </DialogContentText>
        ) : (
          <DialogContentText component="div">
            <Typography variant="body2" sx={{ mb: 2 }}>{confirmation}</Typography>
            <Box sx={{ mb: 2 }}>
              <TargetList items={files} />
            </Box>
            {skipped > 0 && (
              <Typography variant="caption" color="text.secondary">
                {plural(skipped, 'selected file')} still being worked on or already being deleted will be left alone.
              </Typography>
            )}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        {!nothingToDelete && (
          <Button onClick={onConfirm} color="error" variant="contained" disabled={busy}>
            {busy ? <CircularProgress size={16} color="inherit" /> : 'Delete'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default BulkDeleteConfirmDialog;
